import { useCurrentUser } from 'hooks/auth';
import { useSearchParams } from 'hooks/routing';
import React, { Fragment, useEffect, useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { pathManager } from 'routes';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  Grid,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import Button, { RouterButton } from '@libs/ui/components/button';
import Typography from '@libs/ui/components/typography';

interface ImportantNotificationsPopoverProps extends React.PropsWithChildren {
  count: number;
}

const STORAGE_KEY = 'important-notifications-count';

const useStorageKey = () => {
  const { user } = useCurrentUser();

  return `${user?.id}-${STORAGE_KEY}`;
};

const useStyles = makeStyles((theme) => ({
  tooltip: {
    background: 'transparent',
    padding: 0,
    margin: '0 !important',
    width: 350,
    maxWidth: 350,
  },
  arrow: {
    color: 'white',
  },
}));

export const ImportantNotificationsPopover = ({
  count,
  children,
}: ImportantNotificationsPopoverProps) => {
  const storageKey = useStorageKey();
  const params = useSearchParams();
  const matchRoute = useRouteMatch({
    path: pathManager.notifications.generatePath(),
    exact: true,
  });
  const classes = useStyles();
  const lsCount = Number(localStorage.getItem(storageKey));
  const [isOpen, setIsOpen] = React.useState(lsCount !== count);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(storageKey, count.toString());
  }, [count, storageKey]);

  const isOnImportantNotificationsPage =
    matchRoute?.isExact && params['type'] === 'important';
  const isOpenFinal = !!count && !isOnImportantNotificationsPage && isOpen;

  useEffect(() => {
    if (isOnImportantNotificationsPage) {
      closePopover();
    }
  }, [isOnImportantNotificationsPage, closePopover]);

  return (
    <Tooltip
      onClose={closePopover}
      open={isOpenFinal}
      disableFocusListener
      disableHoverListener
      arrow
      disableTouchListener
      classes={{
        tooltip: classes.tooltip,
        arrow: classes.arrow,
      }}
      title={
        <Fragment>
          <Card elevation={8}>
            <CardContent>
              <Typography variant="body1" component="div" textAlign="center">
                You have {count} important notification(s)
              </Typography>
            </CardContent>
            <CardActions>
              <Box mx={2} width="100%" mb={2}>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <RouterButton
                      to={{
                        pathname: pathManager.notifications.generatePath(),
                        search: '?type=important',
                      }}
                      fullWidth
                      variant="contained"
                      color="info"
                    >
                      check it
                    </RouterButton>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="info"
                      onClick={closePopover}
                    >
                      Close this
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardActions>
          </Card>
        </Fragment>
      }
    >
      <div>{children}</div>
    </Tooltip>
  );
};
